import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useBlocker, useLocation } from '@tanstack/react-router'
import RealtimeKitClient from '@cloudflare/realtimekit'
import { RealtimeKitProvider } from '@cloudflare/realtimekit-react'
import type { MeetingEndReason } from '@hub/contracts'
import { orpc } from '@/lib/orpc-client'
import {
  clearStoredSession,
  IDLE_SESSION,
  isProjectExit,
  isSessionLive,
  readStoredSession,
  reduceSession,
  writeStoredSession,
  type SessionState,
  type StoredSession,
} from './meeting-session'
import {
  EMPTY_ROOM_STATE,
  MeetingRoomClient,
  type AudioSegment,
  type MeetingRoomState,
} from './meeting-client'
import { MicTranscriber } from './transcriber'
import { applyDevice, NO_MEDIA, useRealtimeKitMediaState, type MediaState } from './media-state'
import { attachAudio, clientOptions, createMediaQueue, type MeetingClient } from './rtk'
import { readRememberedDevices } from './devices'

/**
 * The meeting session provider (board fqw7): one per project layout.
 *
 * It owns what used to die with the `/meet/$code` route — the RealtimeKit
 * client, the room socket, the microphone transcriber and the remote audio
 * — so navigating to a chat or a document within the project keeps you in
 * the meeting, and the route is a view over the session. It also owns the
 * three ways out: the Leave button, the confirmed switch to another project
 * (a router blocker), and the host's end; and the way back in after a
 * reload — the session is kept in `sessionStorage` and re-joined as the
 * same participant on mount.
 */

/** What `meetings.join` answered, minus the socket path. */
export interface JoinInput {
  projectId: string
  code: string
  meeting: { id: string; title: string }
  self: { userId: string; name: string; isHost: boolean; canEnd: boolean }
  notesDocumentId: string | null
  rtk: { authToken: string; meetingId: string } | null
}

export interface JoinChoice {
  micOn: boolean
  cameraOn: boolean
  audioDeviceId?: string
  videoDeviceId?: string
}

export interface RoomActions {
  /** Host only; see `MeetingRoomClient.setTranscriptionPaused`. */
  setTranscriptionPaused: (paused: boolean) => void
  setHandRaised: (raised: boolean) => void
  lowerHand: (userId: string) => void
}

export interface MeetingSessionValue {
  state: SessionState
  media: MediaState
  room: MeetingRoomState
  roomActions: RoomActions
  /** The SDK client for `code` is initialised (the pre-join can publish). */
  isPrepared: (code: string) => boolean
  /** Initialise the SDK for a meeting about to be joined (the pre-join screen). */
  prepare: (input: JoinInput) => Promise<void>
  /** Drop a prepared, never joined client (pre-join cancelled). */
  cancelPrepare: () => void
  /** Publish media and connect the room. Resolves with what the devices refused. */
  join: (input: JoinInput, choice: JoinChoice) => Promise<{ audio: boolean; video: boolean }>
  /** Stop media, close the socket, forget the session. */
  leave: () => Promise<void>
  /** The host ended it from this tab: settle locally without waiting for the socket. */
  markEnded: (reason: MeetingEndReason) => void
  /** A blocked navigation out of the project while in a meeting. */
  exit: { pending: boolean; confirm: () => void; cancel: () => void }
}

const MeetingSessionContext = createContext<MeetingSessionValue | null>(null)

const newClientId = () => Math.random().toString(36).slice(2, 12)

export function MeetingSessionProvider({
  projectId,
  children,
}: {
  projectId: string
  children: ReactNode
}) {
  // A stored session is 'reconnecting' from the very first render, so the
  // route (whose effects run before this provider's) never starts its own
  // join — that would mint a second token while the reconnect is under way.
  const [state, dispatch] = useReducer(reduceSession, projectId, (id) => {
    const stored = readStoredSession(sessionStorage, id)
    return stored
      ? reduceSession(IDLE_SESSION, { type: 'reconnect', session: stored })
      : IDLE_SESSION
  })
  const [client, setClient] = useState<MeetingClient | null>(null)
  const [media, setMedia] = useState<MediaState>(NO_MEDIA)
  const [room, setRoom] = useState<MeetingRoomState>(EMPTY_ROOM_STATE)

  /** The SDK client and which meeting it was minted for. */
  const clientRef = useRef<{ code: string; client: MeetingClient } | null>(null)
  /** An init in flight, so React 19's double effects and a fast double click share it. */
  const preparing = useRef<{ code: string; promise: Promise<MeetingClient | null> } | null>(null)
  const roomRef = useRef<MeetingRoomClient | null>(null)
  const stateRef = useRef(state)
  stateRef.current = state
  // All self-media calls are serialised — RealtimeKit throws on overlapping
  // calls to the same method.
  const enqueue = useRef(createMediaQueue()).current

  // ── SDK client ──────────────────────────────────────────────────────────

  const dropClient = useCallback(async (reason: 'leave' | 'discard') => {
    const current = clientRef.current
    clientRef.current = null
    preparing.current = null
    setClient(null)
    setMedia(NO_MEDIA)
    if (!current) return
    try {
      // Leaving tells the SFU to drop our peer at once; a discarded pre-join
      // client never joined, but the SDK opened its room socket on init.
      await current.client.leave()
    } catch (err) {
      if (reason === 'leave') console.warn('[meet] leave failed', err)
    }
  }, [])

  const initClient = useCallback(
    async (code: string, rtk: JoinInput['rtk']): Promise<MeetingClient | null> => {
      if (!rtk) return null
      if (clientRef.current?.code === code) return clientRef.current.client
      if (preparing.current?.code === code) return preparing.current.promise
      // A client for another meeting is in the way: leave it first.
      if (clientRef.current) await dropClient('leave')
      const promise = RealtimeKitClient.init(
        clientOptions(rtk.authToken, (err) => console.error('[meet] RealtimeKit error', err))
      ).then(
        (created) => {
          if (preparing.current?.promise !== promise) {
            // Cancelled while initialising: nobody wants this client.
            void created.leave().catch(() => {})
            return null
          }
          preparing.current = null
          clientRef.current = { code, client: created }
          setClient(created)
          return created
        },
        (err) => {
          if (preparing.current?.promise === promise) preparing.current = null
          throw err
        }
      )
      preparing.current = { code, promise }
      return promise
    },
    [dropClient]
  )

  // ── Room socket ─────────────────────────────────────────────────────────

  const closeRoom = useCallback((explicit: boolean) => {
    const current = roomRef.current
    roomRef.current = null
    if (explicit) current?.leave()
    else current?.close()
    setRoom(EMPTY_ROOM_STATE)
  }, [])

  const settleEnded = useCallback(
    (reason: MeetingEndReason, notesDocumentId: string | null) => {
      closeRoom(false)
      clearStoredSession(sessionStorage)
      void dropClient('leave')
      dispatch({ type: 'ended', reason, notesDocumentId })
    },
    [closeRoom, dropClient]
  )

  const openRoom = useCallback(
    (session: StoredSession) => {
      closeRoom(false)
      const socket = new MeetingRoomClient(session.projectId, session.meeting.id, session.clientId)
      roomRef.current = socket
      socket.subscribe((next) => {
        setRoom(next)
        if (next.status === 'ended' && roomRef.current === socket) {
          settleEnded(next.endedReason ?? 'host', next.notesDocumentId)
        }
      })
      socket.connect()
    },
    [closeRoom, settleEnded]
  )

  // ── Join / reconnect / leave ────────────────────────────────────────────

  const publish = useCallback(
    async (sdk: MeetingClient, choice: JoinChoice): Promise<{ audio: boolean; video: boolean }> => {
      const denied = { audio: false, video: false }
      // Always both, explicitly (board m5gc #11): RealtimeKit would otherwise
      // publish the device it remembers in localStorage from an earlier session.
      await enqueue(async () => {
        await applyDevice(sdk, 'audio', choice.audioDeviceId)
        await applyDevice(sdk, 'video', choice.videoDeviceId)
      })
      await sdk.join()
      // Each device separately, so one refusal is reported as itself rather
      // than hiding the other (REVIEW-2 #5).
      if (choice.micOn) denied.audio = !(await enqueue(() => sdk.self.enableAudio()))
      if (choice.cameraOn) denied.video = !(await enqueue(() => sdk.self.enableVideo()))
      return denied
    },
    [enqueue]
  )

  const prepare = useCallback(
    async (input: JoinInput) => {
      await initClient(input.code, input.rtk)
    },
    [initClient]
  )

  const cancelPrepare = useCallback(() => {
    if (isSessionLive(stateRef.current)) return
    void dropClient('discard')
  }, [dropClient])

  const leave = useCallback(async () => {
    const current = stateRef.current
    if (!isSessionLive(current) || !current.session) return
    dispatch({ type: 'leave' })
    const { projectId: sessionProject, code } = current.session
    closeRoom(true)
    clearStoredSession(sessionStorage)
    // Best effort, in parallel: the socket's goodbye may not have made it
    // out, and the SFU should drop our peer either way.
    await Promise.allSettled([
      orpc.meetings.leave({ projectId: sessionProject, code, reason: 'left' }),
      dropClient('leave'),
    ])
    dispatch({ type: 'left' })
  }, [closeRoom, dropClient])

  const join = useCallback(
    async (input: JoinInput, choice: JoinChoice) => {
      // Joining another meeting leaves this one first.
      if (isSessionLive(stateRef.current) && stateRef.current.session?.code !== input.code) {
        await leave()
      }
      const session: StoredSession = {
        v: 1,
        projectId: input.projectId,
        code: input.code,
        clientId: newClientId(),
        meeting: input.meeting,
        self: input.self,
        notesDocumentId: input.notesDocumentId,
        rtk: input.rtk,
        micOn: choice.micOn,
        cameraOn: choice.cameraOn,
      }
      dispatch({ type: 'join', session })
      try {
        const sdk = await initClient(input.code, input.rtk)
        const denied = sdk ? await publish(sdk, choice) : { audio: false, video: false }
        // Left (or ended) while the media was coming up: nothing to open.
        if (stateRef.current.session?.clientId !== session.clientId) return denied
        writeStoredSession(sessionStorage, {
          ...session,
          micOn: choice.micOn && !denied.audio,
          cameraOn: choice.cameraOn && !denied.video,
        })
        openRoom(session)
        dispatch({ type: 'joined' })
        return denied
      } catch (err) {
        dispatch({ type: 'join-failed' })
        void dropClient('discard')
        throw err
      }
    },
    [dropClient, initClient, leave, openRoom, publish]
  )

  // Reload / tab restore (board fqw7): the stored session is re-joined with
  // the same token — the same RealtimeKit participant — and the room sees
  // the same client id within its grace window, i.e. a resume.
  const reconnectStarted = useRef(false)
  useEffect(() => {
    if (reconnectStarted.current) return
    reconnectStarted.current = true
    const initial = stateRef.current
    if (initial.status !== 'reconnecting' || !initial.session) return
    const stored = initial.session
    void (async () => {
      try {
        const sdk = await initClient(stored.code, stored.rtk)
        if (sdk) {
          const remembered = readRememberedDevices()
          await publish(sdk, {
            micOn: stored.micOn,
            cameraOn: stored.cameraOn,
            audioDeviceId: remembered.audioDeviceId,
            videoDeviceId: remembered.videoDeviceId,
          })
        }
        // Left while reconnecting: the session is gone, do not open a room.
        if (stateRef.current.session?.clientId !== stored.clientId) return
        openRoom(stored)
        dispatch({ type: 'joined' })
      } catch (err) {
        console.error('[meet] could not reconnect to the meeting', err)
        clearStoredSession(sessionStorage)
        void dropClient('discard')
        dispatch({ type: 'join-failed' })
      }
    })()
  }, [projectId, initClient, publish, openRoom, dropClient])

  const markEnded = useCallback(
    (reason: MeetingEndReason) => {
      if (!isSessionLive(stateRef.current)) return
      settleEnded(reason, stateRef.current.session?.notesDocumentId ?? null)
    },
    [settleEnded]
  )

  // ── Keeping the stored session current ──────────────────────────────────

  const live = isSessionLive(state)
  useEffect(() => {
    if (state.status !== 'active' || !client) return
    dispatch({ type: 'media', micOn: media.micOn, cameraOn: media.cameraOn })
  }, [state.status, client, media.micOn, media.cameraOn])

  useEffect(() => {
    if (state.status === 'active' && state.session) {
      writeStoredSession(sessionStorage, state.session)
    }
  }, [state.status, state.session])

  // ── Route awareness ─────────────────────────────────────────────────────

  const { pathname } = useLocation()
  useEffect(() => {
    dispatch({ type: 'route', pathname })
  }, [pathname, state.session?.code])

  // ── Leaving the project = leaving the meeting ───────────────────────────

  const liveRef = useRef(live)
  liveRef.current = live
  const shouldBlockFn = useCallback(
    ({ next }: { next: { pathname: string } }) =>
      liveRef.current && isProjectExit(projectId, next.pathname),
    [projectId]
  )
  const blocker = useBlocker({
    shouldBlockFn,
    withResolver: true,
    // A reload is a reconnect, not a leave — no native "leave site?" prompt.
    enableBeforeUnload: false,
  })
  const exit = useMemo(
    () => ({
      pending: blocker.status === 'blocked',
      confirm: () => {
        void leave().finally(() => blocker.proceed?.())
      },
      cancel: () => blocker.reset?.(),
    }),
    [blocker, leave]
  )

  // ── Tab going away ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!live || !state.session) return
    const { projectId: sessionProject, code } = state.session
    const onPageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return
      // The room learns we are gone now instead of by socket timeout; a
      // reload reconnects within the grace and is a resume, not a leave.
      const body = new Blob(
        [JSON.stringify({ json: { projectId: sessionProject, code, reason: 'unload' } })],
        { type: 'application/json' }
      )
      try {
        navigator.sendBeacon('/rpc/meetings/leave', body)
      } catch {
        // Best effort.
      }
      // Drop our media peer at once, so a reload never overlaps its own
      // previous self (an echo for everyone else). The token stays valid.
      void clientRef.current?.client.leave().catch(() => {})
    }
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [live, state.session])

  // The layout unmounted with a live session (logout, a project that
  // vanished): leave, unless this is StrictMode's remount.
  const mounted = useRef(false)
  const leaveRef = useRef(leave)
  leaveRef.current = leave
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      setTimeout(() => {
        if (!mounted.current && liveRef.current) void leaveRef.current()
      }, 0)
    }
  }, [])

  // ── Transcriber (PLAN-085 §1.6) ─────────────────────────────────────────
  // This tab's own microphone, segmented on silence and sent to the room.
  // Lives here so leaving the route does not stop the transcript.
  const transcriberRef = useRef<MicTranscriber | null>(null)
  const sendAudio = useCallback((segment: AudioSegment) => roomRef.current?.sendAudio(segment), [])
  useEffect(() => {
    const track = media.audioTrack
    if (!track || !live) return
    const transcriber = new MicTranscriber(sendAudio)
    transcriberRef.current = transcriber
    void transcriber.start(track)
    return () => {
      transcriberRef.current = null
      void transcriber.stop()
    }
  }, [media.audioTrack, live, sendAudio])

  // Muting stops the upload entirely — nothing anyone says while muted is
  // transcribed, stored or summarised. So does a paused room (REVIEW-1 #4).
  const transcribing = media.micOn && room.transcription !== 'paused'
  useEffect(() => {
    transcriberRef.current?.setEnabled(transcribing)
  }, [transcribing])

  const roomActions = useMemo<RoomActions>(
    () => ({
      setTranscriptionPaused: (paused) => roomRef.current?.setTranscriptionPaused(paused),
      setHandRaised: (raised) => roomRef.current?.setHand(raised),
      lowerHand: (userId) => roomRef.current?.setHand(false, userId),
    }),
    []
  )

  const isPrepared = useCallback((code: string) => clientRef.current?.code === code, [])

  const value = useMemo<MeetingSessionValue>(
    () => ({
      state,
      media,
      room,
      roomActions,
      isPrepared,
      prepare,
      cancelPrepare,
      join,
      leave,
      markEnded,
      exit,
    }),
    [
      state,
      media,
      room,
      roomActions,
      isPrepared,
      prepare,
      cancelPrepare,
      join,
      leave,
      markEnded,
      exit,
    ]
  )

  // The bridge subtree is one element per client: a re-render of this
  // provider must not re-render `RealtimeKitProvider` (whose context value is
  // a fresh object each time), or the selectors would produce a new media
  // object → setMedia → re-render → … until React gives up.
  const bridge = useMemo(
    () =>
      client ? (
        <RealtimeKitProvider value={client}>
          <MediaBridge onChange={setMedia} />
        </RealtimeKitProvider>
      ) : null,
    [client]
  )

  return (
    <MeetingSessionContext.Provider value={value}>
      {bridge}
      <MeetingAudio remotes={media.remotes} />
      {children}
    </MeetingSessionContext.Provider>
  )
}

export function useMeetingSession(): MeetingSessionValue {
  const value = useContext(MeetingSessionContext)
  if (!value) throw new Error('useMeetingSession must be used within MeetingSessionProvider')
  return value
}

/**
 * Reads the SDK's state and hands it up — the hooks may only run under the
 * RealtimeKit provider. It re-renders on the SDK's own update stream only
 * (see `bridge` above), and every render is one media snapshot.
 */
function MediaBridge({ onChange }: { onChange: (media: MediaState) => void }) {
  const media = useRealtimeKitMediaState()
  useEffect(() => {
    onChange(media)
  }, [media, onChange])
  return null
}

/**
 * Remote audio (board fqw7): one hidden `<audio>` per participant, owned by
 * the session so the meeting stays audible on every route. The core SDK
 * does not play remote audio for you — the UI kit's `RtkParticipantsAudio`
 * normally does, and we are not using the UI kit.
 */
function MeetingAudio({ remotes }: { remotes: MediaState['remotes'] }) {
  return (
    <>
      {remotes.map((participant) => (
        <RemoteAudio key={participant.id} track={participant.audioTrack} />
      ))}
    </>
  )
}

function RemoteAudio({ track }: { track: MediaStreamTrack | null }) {
  const ref = useRef<HTMLAudioElement>(null)
  useEffect(() => {
    if (ref.current) attachAudio(ref.current, track)
  }, [track])
  return <audio ref={ref} autoPlay data-testid="remote-audio" />
}
